import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Ensure axios sends cookies for session-based auth
axios.defaults.withCredentials = true;

const diagramService = {
  // Generate diagram using Microsoft Graph API (server pulls token from session)
  async generateDiagram(params) {
    try {
      const response = await axios.post(`${API_BASE_URL}/diagrams/generate`, {
        ...params // no accessToken needed; session cookie used
      }, { withCredentials: true });
      return response.data;
    } catch (error) {
      // Bubble up full error response for diagnostics (e.g., sessionKeys)
      if (error.response?.data) {
        const err = new Error(error.response.data.error || 'Failed to generate diagram');
        err.payload = error.response.data;
        throw err;
      }
      throw new Error('Failed to generate diagram');
    }
  },

  // Generate diagram using offline data
  async generateOfflineDiagram(params) {
    try {
      const response = await axios.post(`${API_BASE_URL}/diagrams/offline`, params, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to generate offline diagram');
    }
  },

  // Upload JSON file for offline processing
  async uploadJsonFile(file) {
    try {
      const formData = new FormData();
      formData.append('jsonFile', file);

      const response = await axios.post(`${API_BASE_URL}/diagrams/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to upload file');
    }
  },

  // Get available policy types
  async getPolicyTypes() {
    try {
      // Session token used if available; legacy bearer header removed
      const response = await axios.get(`${API_BASE_URL}/diagrams/policy-types`, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get policy types');
    }
  },

  // Get available application types
  async getApplicationTypes() {
    try {
      const response = await axios.get(`${API_BASE_URL}/diagrams/application-types`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get application types');
    }
  },

  // Get available scope tags
  async getScopeTags() {
    try {
      const response = await axios.get(`${API_BASE_URL}/diagrams/scope-tags`, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get scope tags');
    }
  },

  // List applications (for selection UI)
  async listApplications(includeAssignments = true) {
    try {
      const response = await axios.get(`${API_BASE_URL}/diagrams/applications?includeAssignments=${includeAssignments}`, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to list applications');
    }
  },

  // List profiles (device configurations + configuration policies)
  async listProfiles(assignedOnly = true) {
    try {
      const response = await axios.get(`${API_BASE_URL}/diagrams/profiles?assignedOnly=${assignedOnly}`, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to list profiles');
    }
  },

  // Validate diagram parameters
  validateParams(params) {
    const errors = [];

    if (!params.type || !['Applications', 'Profiles'].includes(params.type)) {
      errors.push('Type must be either Applications or Profiles');
    }

    if (params.groupBy && !['Name', 'Assignments'].includes(params.groupBy)) {
      errors.push('GroupBy must be either Name or Assignments');
    }

    if (params.direction && !['TB', 'TD', 'BT', 'LR', 'RL'].includes(params.direction)) {
      errors.push('Direction must be one of: TB, TD, BT, LR, RL');
    }

    if (params.operatingSystem && !Array.isArray(params.operatingSystem)) {
      errors.push('OperatingSystem must be an array');
    }

    if (params.operatingSystem) {
      const validOS = ['Windows', 'macOS', 'iOS', 'Android'];
      const invalidOS = params.operatingSystem.filter(os => !validOS.includes(os));
      if (invalidOS.length > 0) {
        errors.push(`Invalid operating systems: ${invalidOS.join(', ')}`);
      }
    }

    if (params.type === 'Applications' && params.applicationType) {
      if (!Array.isArray(params.applicationType)) {
        errors.push('ApplicationType must be an array');
      } else {
        const invalidAppTypes = params.applicationType.filter(t => typeof t !== 'string' || !t.trim());
        if (invalidAppTypes.length > 0) {
          errors.push('ApplicationType entries must be non-empty strings');
        }
      }
    }

    if (params.type === 'Profiles' && params.policyType) {
      if (!Array.isArray(params.policyType)) {
        errors.push('PolicyType must be an array');
      } else {
        const invalidPolicyTypes = params.policyType.filter(t => typeof t !== 'string' || !t.trim());
        if (invalidPolicyTypes.length > 0) {
          errors.push('PolicyType entries must be non-empty strings');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Get default parameters
  getDefaultParams() {
    return {
      type: 'Applications',
      groupBy: 'Name',
      operatingSystem: ['Windows', 'macOS', 'iOS', 'Android'],
      direction: 'TB',
      displayIcons: true,
      policyType: [],
      applicationType: [],
      appendVersion: false,
      excludeSupersededApps: false
    };
  }
};

export default diagramService;